import React, { useMemo } from "react";
import "./AllSolutionsHero.css";
import { FaArrowDown } from "react-icons/fa6";

import bgImage from "../../../../assets/images/allsolutions/allshero.jpg";
import { resolveMediaUrl } from "../../../../utils/media";

const fallbackAllSolutionsHero = {
  tag: "SOLUTIONS",
  titleLine1: "Integrated Systems",
  titleLine2: "for Every Construction Need",
  ctaText: "Explore all solutions",
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const hasSectionData = (section) => {
  if (!section || typeof section !== "object") return false;
  return Object.values(section).some((value) => {
    if (value === null || typeof value === "undefined") return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  });
};

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceHeroPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") return tryParseJsonObject(raw) || null;
  if (typeof raw !== "object") return null;
  let base = { ...raw };
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...raw, ...raw.data };
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return base;
};

const AllSolutionsHero = ({ content }) => {
  const view = useMemo(() => {
    const merged = coalesceHeroPayload(content);
    const section = hasSectionData(merged) ? merged : fallbackAllSolutionsHero;
    const singleTitle = pickString(section.title);
    let titleLine1 = pickString(section.titleLine1, section.heading);
    let titleLine2 = pickString(section.titleLine2, section.subheading);
    if (!titleLine1 && singleTitle) {
      if (singleTitle.includes("\n")) {
        const parts = singleTitle.split("\n").map((p) => p.trim());
        titleLine1 = parts[0] || "";
        titleLine2 = parts.slice(1).join(" ").trim() || titleLine2;
      } else {
        titleLine1 = singleTitle;
      }
    }
    return {
      tag: pickString(section.tag, section.sectionTag) || fallbackAllSolutionsHero.tag,
      titleLine1: titleLine1 || fallbackAllSolutionsHero.titleLine1,
      titleLine2:
        titleLine2 || (singleTitle && !singleTitle.includes("\n") ? "" : fallbackAllSolutionsHero.titleLine2),
      ctaText: pickString(section.ctaText, section.linkText, section.exploreText) || fallbackAllSolutionsHero.ctaText,
      imageSrc:
        resolveMediaUrl(section.image || section.bgImage || section.backgroundImage || section.heroImage || section.mediaUrl || section.photo) ||
        bgImage,
    };
  }, [content]);

  const scrollToContent = () => {
    document.getElementById("allsolution-guide")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="allsolutionsHero" aria-label="All solutions">
      <div className="allsolutionsHero-bg">
        <img
          src={view.imageSrc}
          alt=""
          className="allsolutionsHero-bgImg"
          draggable={false}
          onError={(e) => {
            if (e.currentTarget.src !== bgImage) {
              e.currentTarget.src = bgImage;
            }
          }}
        />
      </div>

      <div className="allsolutionsHero-overlay" aria-hidden="true" />

      <div className="allsolutionsHero-inner">
        <span className="allsolutionsHero-tag">{view.tag}</span>

        <h1 className="allsolutionsHero-title">
          {view.titleLine1} <br />
          <span className="greytext">{view.titleLine2}</span>
        </h1>

        <div className="allsolutionsHero-ctaRow">
          <button
            type="button"
            className="allsolutionsHero-cta"
            onClick={scrollToContent}
          >
            {view.ctaText}
            <FaArrowDown  className="allsolutionsHero-ctaIcon" aria-hidden />
          </button>
        </div>
      </div>

 
    </section>
  );
};

export default AllSolutionsHero;
